import { AutocompleteContext } from '@jupiterone/query-language-autocomplete';
import { AutocompleteInput } from '@jupiterone/query-language-autocomplete/dist/src/types';
import { getAccountIdFromUrl } from '@jupiterone/jupiter-web-toolkit/src/util/url';
import autocompleteData, {
  DEFAULT_AUTOCOMPLETE_DATA,
  isAutocompleteData,
} from '../autocompleteData';
import { localStorageData } from '../backingStore';
import fetchAceSuggestions from './fetchAceSuggestions';
import { AceCompletion } from './types';

const getAceCompletions: AceCompletion['getCompletions'] = (
  editor,
  session,
  pos,
  prefix,
  callback
) => {
  const { data: immediateContext, promise } = autocompleteData.getData({
    accountId: getAccountIdFromUrl() || 'default',
    backing: localStorageData(
      'autocomplete',
      DEFAULT_AUTOCOMPLETE_DATA,
      isAutocompleteData
    ),
  });

  const initialContext = autocompleteData.contextFromRaw(immediateContext);

  const input: AutocompleteInput = {
    text: editor.getValue(),
    cursorPosition: session.doc.positionToIndex(pos),
    onLoadMetadata: ({ entities }) => {
      // NOTE: Preload then cache properties and relationships
      Promise.all(
        ([] as Promise<any>[]).concat(
          ...Array.from(entities).map((e) => [
            initialContext.relationships(e),
            initialContext.properties(e),
          ])
        )
      ).catch(console.error);
    },
  };

  // JPT-3133
  // This issue is a temporary workaround because of how the Ace editor doesn’t
  // ask for autocomplete suggestions once it gets a list (until none of those
  // values match and the next character is typed).
  const QUOTE_MARKS = ['"', "'"];
  const { text, cursorPosition } = input;
  const index = cursorPosition || text.length - 1;
  if (QUOTE_MARKS.includes(text.slice(index - 1, index))) {
    callback(null, []);
    return;
  }

  const returnSuggestions = (context: AutocompleteContext) =>
    fetchAceSuggestions(prefix, input, context).then((suggestions) =>
      callback(null, suggestions)
    );

  // Immediately make suggestions but fetch data for next call
  void returnSuggestions(initialContext);

  promise
    .then(({ result: rawContext, isNewData }) => {
      // Only return new suggestions if did make remote call
      if (isNewData) {
        void returnSuggestions(autocompleteData.contextFromRaw(rawContext));
      }
    })
    // Log errors but continue on
    .catch(console.error);
};

export default getAceCompletions;
